import * as React from 'react';
import HoverIntent from '@liveauctioneers/react-hoverintent';
import ImageChildren from '../ItemGallery/ImageChildren';
import ImageWithFallback from '@liveauctioneers/caterwaul-components/lib/ImageWithFallback/ImageWithFallback2';
import isEqual from 'react-fast-compare';
import { Ref } from '@/types/Ref';
import styled from 'styled-components';

type Props = {
    className: string;
    imageAlt: string;
    imageUrl: string;
    itemId: number;
    onClick: (event: React.MouseEvent<HTMLImageElement, MouseEvent>) => void;
    placeholderImageUrl: string;
    resetFlag: boolean;
};

const ImageZoom = ({ className = '', imageAlt, imageUrl, itemId, onClick, placeholderImageUrl, resetFlag }: Props) => {
    const targetRef: Ref = React.useRef<HTMLDivElement>();
    const [hover, setHover] = React.useState<boolean>(false);
    const [zoom, setZoom] = React.useState<number>(1);
    const [transformOrigin, setTransformOrigin] = React.useState<string>('');

    React.useEffect(() => {
        setHover(false);
        setZoom(1);
    }, [imageUrl]);

    React.useEffect(() => {
        setHover(false);
        setZoom(1);
    }, [resetFlag]);

    const onMouseOver = React.useCallback(() => {
        if (targetRef) {
            const { naturalHeight, offsetHeight: imageHeight } = targetRef?.current?.children?.[0].children?.[0] || {
                naturalHeight: 0,
                offsetHeight: 0,
            };
            if (naturalHeight > 0 && imageHeight > 0) {
                const zoom = naturalHeight / imageHeight;
                setHover(true);
                setZoom(zoom);
            }
        }
    }, []);

    const onMouseOut = React.useCallback(() => {
        setHover(false);
        setZoom(1);
    }, []);

    const onMouseMove = React.useCallback((event: any) => {
        if (targetRef) {
            const { height, left, top, width } = targetRef?.current?.getBoundingClientRect();

            // depending on the browser it could be body or html
            const offset = {
                left: left + (document.body?.scrollLeft || document.querySelector('html')?.scrollLeft),
                top: top + (document.body?.scrollTop || document.querySelector('html')?.scrollTop),
            };

            setTransformOrigin(
                `${((event.pageX - offset.left) / width) * 100}% ${((event.pageY - offset.top) / height) * 100}%`
            );
        }
    }, []);

    const generateZoomStyles = React.useCallback(() => {
        return {
            transform: `scale(${zoom})`,
            transformOrigin,
        };
    }, [transformOrigin, zoom]);

    const zoomStyles = hover ? generateZoomStyles() : {};

    return (
        <StyledImageZoom className={className} onMouseMove={onMouseMove} ref={(ref) => (targetRef.current = ref)}>
            <HoverIntent interval={50} onMouseOut={onMouseOut} onMouseOver={onMouseOver} sensitivity={5} timeout={50}>
                <div>
                    <StyledImage
                        alt={imageAlt}
                        onClick={onClick}
                        placeholder={placeholderImageUrl}
                        src={imageUrl}
                        style={zoomStyles}
                    />
                </div>
            </HoverIntent>
            <ImageChildren itemId={itemId} />
        </StyledImageZoom>
    );
};

export default React.memo<Props>(ImageZoom, isEqual);

const StyledImageZoom = styled.div`
    flex: 1 1 auto;
    position: relative;
    max-height: 360px;
    overflow: hidden;
    cursor: zoom-in;
    display: block;
    text-align: center;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        max-height: 550px;
    }
`;

const StyledImage = styled(ImageWithFallback)`
    bottom: 0;
    display: block;
    left: 0;
    margin: auto;
    max-height: 100%;
    max-width: 100%;
    position: absolute;
    right: 0;
    top: 0;
    transition: transform 0.25s ease-out;
    z-index: 5;
`;
